import { isoToDatetimeLocal } from './adminProspectDisplay.js';
import {
  ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR,
  parseAdminProspectDatetimeLocal,
} from './adminProspectQuickFollowUp.js';

export const QUICK_FOLLOW_UP_CONTROLS = Object.freeze(['status', 'nextFollowUpAt', 'lastContactAt']);

function normalizeStatus(value) {
  return String(value || 'new').trim() || 'new';
}

function normalizeIso(value) {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return null;
  return date.toISOString();
}

function sameIso(a, b) {
  return normalizeIso(a) === normalizeIso(b);
}

function serverValue(prospect, control) {
  if (control === 'status') return normalizeStatus(prospect?.status);
  if (control === 'nextFollowUpAt') return normalizeIso(prospect?.nextFollowUpAt);
  if (control === 'lastContactAt') return normalizeIso(prospect?.lastContactAt);
  return null;
}

function draftValue(state, control) {
  if (control === 'status') return normalizeStatus(state.drafts.status);
  if (control === 'nextFollowUpAt') {
    const parsed = parseAdminProspectDatetimeLocal(state.drafts.nextFollowUpAtLocal);
    return parsed.ok ? parsed.iso : state.drafts.nextFollowUpAtLocal;
  }
  if (control === 'lastContactAt') {
    const parsed = parseAdminProspectDatetimeLocal(state.drafts.lastContactAtLocal);
    return parsed.ok ? parsed.iso : state.drafts.lastContactAtLocal;
  }
  return null;
}

function emptyControlUi() {
  return { saving: false, success: false, errorKey: null, errorKind: null };
}

export function createQuickFollowUpFormState(prospect) {
  return {
    prospectId: String(prospect?.id || '').trim(),
    visitId: 1,
    baselines: {
      status: normalizeStatus(prospect?.status),
      nextFollowUpAt: normalizeIso(prospect?.nextFollowUpAt),
      lastContactAt: normalizeIso(prospect?.lastContactAt),
      versionToken: prospect?.updatedAtVersion || prospect?.updatedAt || null,
    },
    drafts: {
      status: normalizeStatus(prospect?.status),
      nextFollowUpAtLocal: isoToDatetimeLocal(prospect?.nextFollowUpAt),
      lastContactAtLocal: isoToDatetimeLocal(prospect?.lastContactAt),
    },
    dirty: { status: false, nextFollowUpAt: false, lastContactAt: false },
    review: { status: null, nextFollowUpAt: null, lastContactAt: null },
    note: {
      body: '',
      status: 'idle',
      errorKey: null,
      uncertainDuplicateRisk: false,
      historyRefreshFailed: false,
      revision: 0,
    },
    controlUi: {
      status: emptyControlUi(),
      nextFollowUpAt: emptyControlUi(),
      lastContactAt: emptyControlUi(),
    },
    controlRevision: { status: 0, nextFollowUpAt: 0, lastContactAt: 0 },
    responseJumpMessageKey: null,
    refreshAfterWrite: null,
  };
}

/** Start a note write; bumps revision so older history-refresh results cannot unlock it. */
export function beginQuickFollowUpNoteWrite(state) {
  const revision = Number(state?.note?.revision || 0) + 1;
  return {
    ...state,
    note: {
      ...state.note,
      revision,
      status: 'pending',
      errorKey: null,
      uncertainDuplicateRisk: false,
      historyRefreshFailed: false,
    },
  };
}

export function markQuickFollowUpNoteWriteSuccess(state, { noteRevision } = {}) {
  if (noteRevision != null && Number(state?.note?.revision || 0) !== Number(noteRevision)) {
    return state;
  }
  return {
    ...state,
    note: {
      body: '',
      status: 'success',
      errorKey: null,
      uncertainDuplicateRisk: false,
      historyRefreshFailed: false,
      revision: state.note.revision,
    },
  };
}

export function markQuickFollowUpNoteWriteError(
  state,
  { noteRevision, errorKey = null, uncertainDuplicateRisk = false } = {},
) {
  if (noteRevision != null && Number(state?.note?.revision || 0) !== Number(noteRevision)) {
    return state;
  }
  return {
    ...state,
    note: {
      ...state.note,
      status: 'error',
      errorKey,
      uncertainDuplicateRisk: Boolean(uncertainDuplicateRisk),
      historyRefreshFailed: false,
    },
  };
}

/**
 * Note write succeeded; activity history refresh did not.
 * Never overwrites a newer note's pending/error write state or clears its draft/error.
 */
export function markQuickFollowUpNoteHistoryRefreshFailure(state, { noteRevision } = {}) {
  const currentRevision = Number(state?.note?.revision || 0);
  const revisionMatches = noteRevision == null || currentRevision === Number(noteRevision);
  const writeInFlight =
    state?.note?.status === 'pending' || state?.note?.status === 'error';

  if (!revisionMatches || writeInFlight) {
    return {
      ...state,
      note: {
        ...state.note,
        historyRefreshFailed: true,
      },
    };
  }

  return {
    ...state,
    note: {
      ...state.note,
      status: 'success',
      errorKey: null,
      uncertainDuplicateRisk: false,
      historyRefreshFailed: true,
    },
  };
}

export function clearQuickFollowUpNoteHistoryRefreshFailure(state, { noteRevision } = {}) {
  const currentRevision = Number(state?.note?.revision || 0);
  if (noteRevision != null && currentRevision !== Number(noteRevision)) {
    // Older op recovered the timeline — clear the banner only; keep newer write status/draft/error.
    return {
      ...state,
      note: {
        ...state.note,
        historyRefreshFailed: false,
      },
    };
  }
  return {
    ...state,
    note: {
      ...state.note,
      historyRefreshFailed: false,
    },
  };
}

export function syncQuickFollowUpFormFromProspect(state, prospect, { forceReset = false } = {}) {
  const nextId = String(prospect?.id || '').trim();
  if (forceReset || !nextId || nextId !== state.prospectId) {
    const fresh = createQuickFollowUpFormState(prospect);
    if (state.prospectId && nextId) fresh.visitId = state.visitId + 1;
    return fresh;
  }

  const next = {
    ...state,
    baselines: { ...state.baselines },
    drafts: { ...state.drafts },
    dirty: { ...state.dirty },
    review: { ...state.review },
    controlUi: {
      status: { ...state.controlUi.status },
      nextFollowUpAt: { ...state.controlUi.nextFollowUpAt },
      lastContactAt: { ...state.controlUi.lastContactAt },
    },
    controlRevision: { ...(state.controlRevision || { status: 0, nextFollowUpAt: 0, lastContactAt: 0 }) },
    note: { ...state.note },
  };

  const serverVersion = prospect?.updatedAtVersion || prospect?.updatedAt || null;
  let fieldConflict = false;

  for (const control of QUICK_FOLLOW_UP_CONTROLS) {
    const incoming = serverValue(prospect, control);
    if (!next.dirty[control]) {
      next.baselines[control] = incoming;
      if (control === 'status') next.drafts.status = incoming;
      if (control === 'nextFollowUpAt') next.drafts.nextFollowUpAtLocal = isoToDatetimeLocal(incoming);
      if (control === 'lastContactAt') next.drafts.lastContactAtLocal = isoToDatetimeLocal(incoming);
      next.review[control] = null;
      continue;
    }

    const baseline = next.baselines[control];
    const changed = control === 'status' ? baseline !== incoming : !sameIso(baseline, incoming);
    if (changed) {
      fieldConflict = true;
      next.review[control] = {
        serverValue: incoming,
        draftValue: draftValue(next, control),
        baselineValue: baseline,
        conflict: true,
      };
    }
  }

  if (!fieldConflict) {
    next.baselines.versionToken = serverVersion;
  }

  return next;
}

export function setQuickFollowUpDraft(state, control, value) {
  const revisions = { ...(state.controlRevision || { status: 0, nextFollowUpAt: 0, lastContactAt: 0 }) };
  revisions[control] = (revisions[control] || 0) + 1;
  const next = {
    ...state,
    drafts: { ...state.drafts },
    dirty: { ...state.dirty },
    review: { ...state.review, [control]: null },
    controlRevision: revisions,
    controlUi: {
      ...state.controlUi,
      [control]: { ...state.controlUi[control], success: false, errorKey: null, errorKind: null },
    },
  };

  if (control === 'status') {
    next.drafts.status = normalizeStatus(value);
    next.dirty.status = next.drafts.status !== next.baselines.status;
  } else if (control === 'nextFollowUpAt') {
    next.drafts.nextFollowUpAtLocal = String(value ?? '');
    const parsed = parseAdminProspectDatetimeLocal(next.drafts.nextFollowUpAtLocal);
    next.dirty.nextFollowUpAt = !(parsed.ok && sameIso(parsed.iso, next.baselines.nextFollowUpAt));
  } else if (control === 'lastContactAt') {
    next.drafts.lastContactAtLocal = String(value ?? '');
    const parsed = parseAdminProspectDatetimeLocal(next.drafts.lastContactAtLocal);
    next.dirty.lastContactAt = !(parsed.ok && sameIso(parsed.iso, next.baselines.lastContactAt));
  }
  return next;
}

export function beginQuickFollowUpSave(state, control) {
  return {
    ...state,
    refreshAfterWrite: null,
    controlUi: {
      ...state.controlUi,
      [control]: { saving: true, success: false, errorKey: null, errorKind: null },
    },
  };
}

export function reconcileQuickFollowUpControl(
  state,
  control,
  savedValue,
  versionToken,
  { requestRevision = null } = {},
) {
  const revisions = state.controlRevision || { status: 0, nextFollowUpAt: 0, lastContactAt: 0 };
  const revisionMoved =
    requestRevision != null && Number(revisions[control] || 0) !== Number(requestRevision);

  const next = {
    ...state,
    baselines: {
      ...state.baselines,
      versionToken: versionToken ?? state.baselines.versionToken,
    },
    drafts: { ...state.drafts },
    dirty: { ...state.dirty },
    review: { ...state.review, [control]: null },
    controlRevision: { ...revisions },
    controlUi: {
      ...state.controlUi,
      [control]: { saving: false, success: true, errorKey: null, errorKind: null },
    },
  };

  if (control === 'status') {
    next.baselines.status = normalizeStatus(savedValue);
    if (revisionMoved) {
      next.dirty.status = next.drafts.status !== next.baselines.status;
    } else {
      next.drafts.status = next.baselines.status;
      next.dirty.status = false;
    }
  } else if (control === 'nextFollowUpAt') {
    next.baselines.nextFollowUpAt = normalizeIso(savedValue);
    if (revisionMoved) {
      const parsed = parseAdminProspectDatetimeLocal(next.drafts.nextFollowUpAtLocal);
      next.dirty.nextFollowUpAt = !(parsed.ok && sameIso(parsed.iso, next.baselines.nextFollowUpAt));
    } else {
      next.drafts.nextFollowUpAtLocal = isoToDatetimeLocal(next.baselines.nextFollowUpAt);
      next.dirty.nextFollowUpAt = false;
    }
  } else if (control === 'lastContactAt') {
    next.baselines.lastContactAt = normalizeIso(savedValue);
    if (revisionMoved) {
      const parsed = parseAdminProspectDatetimeLocal(next.drafts.lastContactAtLocal);
      next.dirty.lastContactAt = !(parsed.ok && sameIso(parsed.iso, next.baselines.lastContactAt));
    } else {
      next.drafts.lastContactAtLocal = isoToDatetimeLocal(next.baselines.lastContactAt);
      next.dirty.lastContactAt = false;
    }
  }
  return next;
}

export function failQuickFollowUpSave(state, control, { errorKey, errorKind } = {}) {
  return {
    ...state,
    controlUi: {
      ...state.controlUi,
      [control]: {
        saving: false,
        success: false,
        errorKey: errorKey || 'admin.prospection.error.failedPrecondition',
        errorKind: errorKind || null,
      },
    },
  };
}

export function markQuickFollowUpRefreshFailure(state, control) {
  return {
    ...state,
    controlUi: {
      ...state.controlUi,
      [control]: {
        ...state.controlUi[control],
        saving: false,
        success: true,
        errorKey: null,
        errorKind: null,
      },
    },
    refreshAfterWrite: {
      control,
      reason: 'post_write',
      messageKey: 'admin.prospection.quickFollowUp.refreshAfterSaveFailed',
    },
  };
}

/**
 * Conflict detail reload failed after a rejected write — not a successful save.
 * Keeps conflict UI (no success:true) and surfaces a distinct retry reason.
 */
export function markQuickFollowUpConflictReloadFailure(state, control) {
  return {
    ...state,
    controlUi: {
      ...state.controlUi,
      [control]: {
        ...state.controlUi[control],
        saving: false,
        success: false,
        errorKey: 'admin.prospection.quickFollowUp.conflict.message',
        errorKind: 'failed-precondition',
      },
    },
    refreshAfterWrite: {
      control,
      reason: 'conflict_reload',
      messageKey: 'admin.prospection.quickFollowUp.conflict.reloadFailed',
    },
  };
}

export function clearQuickFollowUpRefreshFailure(state) {
  return { ...state, refreshAfterWrite: null };
}

/**
 * After a successful addAdminProspectNote, reload activity history only.
 * On applied timeline failure, reveal Follow-up (mobile) via attention — does not replay the note write.
 * CRM post-save refresh must NOT use this helper (panel already handles CRM refresh-failure attention).
 */
export async function runQuickNotePostSaveTimelineRefresh({
  ownerProspectId,
  requestVisitId,
  getActiveProspectId,
  getActiveVisitId,
  getMounted,
  loadTimeline,
  requestSectionAttention,
} = {}) {
  const ownership = () =>
    shouldRunProspectRefreshStep({
      ownerProspectId,
      requestVisitId,
      getActiveProspectId,
      getActiveVisitId,
      getMounted,
    });

  if (!ownership().ok) {
    return {
      ok: false,
      applied: false,
      noteSaved: true,
      timelineOk: null,
      didReveal: false,
      reason: 'stale',
    };
  }

  const timelineResult = await loadTimeline({
    ownerProspectId,
    visitId: requestVisitId,
  });

  if (!ownership().ok) {
    return {
      ok: false,
      applied: false,
      noteSaved: true,
      timelineOk: null,
      didReveal: false,
      reason: 'stale',
    };
  }

  const timelineOk = Boolean(timelineResult?.ok);
  const applied = Boolean(timelineResult?.applied);
  let didReveal = false;

  if (applied && !timelineOk && typeof requestSectionAttention === 'function') {
    const attention = requestSectionAttention({
      sectionId: 'followUp',
      ownerProspectId,
      visitId: requestVisitId,
      reason: 'timeline-refresh-failure',
    });
    didReveal = Boolean(attention?.didExpand);
  }

  return {
    ok: timelineOk,
    applied,
    noteSaved: true,
    timelineOk,
    didReveal,
  };
}

/**
 * Timeline pagination / append load. Reveals Follow-up when an applied failure lands while collapsed.
 * Successful loads never force reopen. Does not notify CRM refresh-failure paths.
 * Callers' loadTimeline must retain busy + ownership (see adminProspectTimelineLoad.js).
 */
export async function runProspectTimelineAppendWithAttention({
  ownerProspectId,
  requestVisitId,
  getActiveProspectId,
  getActiveVisitId,
  getMounted,
  loadTimeline,
  pageToken,
  requestSectionAttention,
} = {}) {
  const ownership = () =>
    shouldRunProspectRefreshStep({
      ownerProspectId,
      requestVisitId,
      getActiveProspectId,
      getActiveVisitId,
      getMounted,
    });

  if (!ownership().ok) {
    return { ok: false, applied: false, didReveal: false, reason: 'stale' };
  }

  const timelineResult = await loadTimeline({
    append: true,
    pageToken,
    ownerProspectId,
    visitId: requestVisitId,
  });

  if (!ownership().ok) {
    return { ok: false, applied: false, didReveal: false, reason: 'stale' };
  }

  if (timelineResult?.reason === 'busy') {
    return { ok: false, applied: false, didReveal: false, reason: 'busy' };
  }

  const timelineOk = Boolean(timelineResult?.ok);
  const applied = Boolean(timelineResult?.applied);
  let didReveal = false;

  if (applied && !timelineOk && typeof requestSectionAttention === 'function') {
    const attention = requestSectionAttention({
      sectionId: 'followUp',
      ownerProspectId,
      visitId: requestVisitId,
      reason: 'timeline-pagination-failure',
    });
    didReveal = Boolean(attention?.didExpand);
  }

  return {
    ok: timelineOk,
    applied,
    timelineOk,
    didReveal,
  };
}

export function enterQuickFollowUpConflictReview(state, control, prospect) {
  const incoming = serverValue(prospect, control);
  return {
    ...state,
    dirty: { ...state.dirty, [control]: true },
    review: {
      ...state.review,
      [control]: {
        serverValue: incoming,
        draftValue: draftValue(state, control),
        baselineValue: state.baselines[control],
        conflict: true,
      },
    },
    baselines: {
      ...state.baselines,
      versionToken: prospect?.updatedAtVersion || prospect?.updatedAt || state.baselines.versionToken,
    },
    controlUi: {
      ...state.controlUi,
      [control]: {
        saving: false,
        success: false,
        errorKey: 'admin.prospection.quickFollowUp.conflict.message',
        errorKind: 'failed-precondition',
      },
    },
  };
}

export function dismissQuickFollowUpReview(state, control, { keepDraft = true } = {}) {
  const review = state.review[control];
  if (!review) return state;
  if (!keepDraft) {
    const cleared = {
      ...state,
      review: { ...state.review, [control]: null },
      dirty: { ...state.dirty, [control]: false },
      baselines: { ...state.baselines, [control]: review.serverValue },
      drafts: { ...state.drafts },
      controlUi: { ...state.controlUi, [control]: emptyControlUi() },
    };
    if (control === 'status') cleared.drafts.status = normalizeStatus(review.serverValue);
    if (control === 'nextFollowUpAt') {
      cleared.drafts.nextFollowUpAtLocal = isoToDatetimeLocal(review.serverValue);
    }
    if (control === 'lastContactAt') {
      cleared.drafts.lastContactAtLocal = isoToDatetimeLocal(review.serverValue);
    }
    return cleared;
  }
  return {
    ...state,
    review: { ...state.review, [control]: null },
    controlUi: { ...state.controlUi, [control]: emptyControlUi() },
  };
}

export function focusProspectPublicResponseSection({
  documentObj,
  locationObj = null,
  anchorId = ADMIN_PROSPECT_PUBLIC_RESPONSE_ANCHOR,
  ensureExpanded = null,
  schedule = typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame
    : (cb) => setTimeout(cb, 0),
} = {}) {
  const hashBefore = locationObj ? String(locationObj.hash || '') : undefined;

  const focusTarget = () => {
    if (!documentObj || typeof documentObj.getElementById !== 'function') {
      return { ok: false, reason: 'unavailable', hashBefore, hashAfter: hashBefore };
    }
    const el = documentObj.getElementById(anchorId);
    if (!el) {
      return { ok: false, reason: 'missing', hashBefore, hashAfter: hashBefore };
    }
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (typeof el.focus === 'function') {
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
    if (locationObj) {
      const hashAfter = String(locationObj.hash || '');
      if (hashAfter !== hashBefore) {
        try {
          locationObj.hash = hashBefore;
        } catch {
          // ignore
        }
      }
    }
    return {
      ok: true,
      hashBefore,
      hashAfter: locationObj ? String(locationObj.hash || '') : undefined,
    };
  };

  let didExpand = false;
  if (typeof ensureExpanded === 'function') {
    try {
      const result = ensureExpanded();
      didExpand = Boolean(result?.didExpand);
    } catch {
      didExpand = false;
    }
  }

  if (didExpand && typeof schedule === 'function') {
    schedule(() => {
      schedule(() => {
        focusTarget();
      });
    });
    return {
      ok: true,
      deferred: true,
      didExpand: true,
      hashBefore,
      hashAfter: hashBefore,
    };
  }

  return { ...focusTarget(), didExpand, deferred: false };
}

export function shouldApplyQuickFollowUpLiveResult({
  ownerProspectId,
  requestVisitId,
  getActiveProspectId,
  getActiveVisitId,
  getMounted,
} = {}) {
  if (typeof getMounted === 'function' && !getMounted()) return false;
  const owner = String(ownerProspectId || '').trim();
  const active = String(typeof getActiveProspectId === 'function' ? getActiveProspectId() : '').trim();
  if (!owner || !active || owner !== active) return false;
  if (requestVisitId != null && typeof getActiveVisitId === 'function') {
    return requestVisitId === getActiveVisitId();
  }
  return true;
}

export function shouldRunProspectRefreshStep({
  ownerProspectId,
  requestVisitId,
  getActiveProspectId,
  getActiveVisitId,
  getMounted,
  requestId = null,
  isCurrentRequest = null,
} = {}) {
  if (typeof getMounted === 'function' && !getMounted()) {
    return { ok: false, reason: 'unmounted' };
  }
  const owner = String(ownerProspectId || '').trim();
  const active = String(typeof getActiveProspectId === 'function' ? getActiveProspectId() : '').trim();
  if (!owner || owner !== active) return { ok: false, reason: 'prospect-mismatch' };
  if (requestVisitId != null && typeof getActiveVisitId === 'function') {
    if (requestVisitId !== getActiveVisitId()) return { ok: false, reason: 'stale-visit' };
  }
  if (requestId != null && typeof isCurrentRequest === 'function' && !isCurrentRequest(requestId)) {
    return { ok: false, reason: 'stale-request' };
  }
  return { ok: true };
}

export function buildFollowUpPatchFields(control, state) {
  if (control === 'status') {
    return { ok: true, fields: { status: normalizeStatus(state.drafts.status) } };
  }
  if (control === 'nextFollowUpAt') {
    const parsed = parseAdminProspectDatetimeLocal(state.drafts.nextFollowUpAtLocal);
    if (!parsed.ok) {
      return { ok: false, errorKey: 'admin.prospection.quickFollowUp.datetime.invalid' };
    }
    return { ok: true, fields: { nextFollowUpAt: parsed.iso }, empty: parsed.empty };
  }
  if (control === 'lastContactAt') {
    const parsed = parseAdminProspectDatetimeLocal(state.drafts.lastContactAtLocal);
    if (!parsed.ok) {
      return { ok: false, errorKey: 'admin.prospection.quickFollowUp.datetime.invalid' };
    }
    return { ok: true, fields: { lastContactAt: parsed.iso }, empty: parsed.empty };
  }
  return { ok: false, errorKey: 'admin.prospection.error.invalid' };
}

export function quickFollowUpConflictErrorKey(kind) {
  if (kind === 'failed-precondition') return 'admin.prospection.quickFollowUp.conflict.message';
  return null;
}

export async function runQuickFollowUpPostSaveRefresh({
  ownerProspectId,
  requestVisitId,
  getActiveProspectId,
  getActiveVisitId,
  getMounted,
  loadDetail,
  loadTimeline,
  conflictReload = false,
  refreshOnly = false,
} = {}) {
  const ownership = () =>
    shouldRunProspectRefreshStep({
      ownerProspectId,
      requestVisitId,
      getActiveProspectId,
      getActiveVisitId,
      getMounted,
    });

  if (!ownership().ok) return { refreshOk: false, applied: false, prospect: null };

  const detailResult = await loadDetail({
    silent: true,
    ownerProspectId,
    visitId: requestVisitId,
  });
  if (!ownership().ok) return { refreshOk: false, applied: false, prospect: null };
  if (!detailResult?.applied) return { refreshOk: false, applied: false, prospect: null };
  if (!detailResult?.ok) {
    return {
      refreshOk: false,
      applied: true,
      prospect: null,
      detailOk: false,
      timelineOk: null,
    };
  }

  // Conflict review only needs the latest detail document.
  if (conflictReload) {
    return {
      refreshOk: true,
      applied: true,
      prospect: detailResult.data || null,
      detailOk: true,
      timelineOk: null,
    };
  }

  // Post-save refresh and refresh retry both reload timeline so a failed
  // timeline stage can recover without replaying the write.
  if (!ownership().ok) return { refreshOk: false, applied: false, prospect: null };

  const timelineResult = await loadTimeline({
    ownerProspectId,
    visitId: requestVisitId,
  });
  if (!ownership().ok) return { refreshOk: false, applied: false, prospect: null };
  const timelineOk = Boolean(timelineResult?.ok);
  return {
    refreshOk: timelineOk,
    applied: Boolean(timelineResult?.applied),
    prospect: detailResult.data || null,
    detailOk: true,
    timelineOk,
  };
}

/**
 * Immutable CRM save request captured at submit time.
 * Does not hold draft snapshots for unrelated controls or note state.
 */
export function captureQuickFollowUpSaveRequest(state, control, { fields, savedValue } = {}) {
  return {
    control,
    fields,
    savedValue,
    versionToken: state.baselines.versionToken,
    requestRevision: (state.controlRevision || {})[control] || 0,
  };
}

/**
 * Shared CRM save orchestration used by the panel and behavioral tests.
 * Begin/reconcile/fail always apply against CURRENT state via applyState.
 */

export async function runQuickFollowUpControlSave({
  applyState,
  mayApply,
  write,
  request,
  requestVisitId,
  classifyError,
  onConflict,
  onError,
} = {}) {
  if (!request?.control || typeof applyState !== 'function' || typeof write !== 'function') {
    return { ok: false, applied: false };
  }
  applyState((current) => beginQuickFollowUpSave(current, request.control));
  try {
    await write({
      fields: request.fields,
      versionToken: request.versionToken,
    });
    if (typeof mayApply === 'function' && !mayApply(requestVisitId)) {
      return { ok: false, applied: false, stale: true };
    }
    applyState((current) =>
      reconcileQuickFollowUpControl(current, request.control, request.savedValue, request.versionToken, {
        requestRevision: request.requestRevision,
      }),
    );
    return { ok: true, applied: true };
  } catch (error) {
    if (typeof mayApply === 'function' && !mayApply(requestVisitId)) {
      return { ok: false, applied: false, stale: true, error };
    }
    const kind = typeof classifyError === 'function' ? classifyError(error) : null;
    if (kind === 'failed-precondition' && typeof onConflict === 'function') {
      await onConflict(error, kind);
      return { ok: false, applied: true, conflict: true, error };
    }
    if (typeof onError === 'function') {
      await onError(error, kind);
    } else {
      applyState((current) =>
        failQuickFollowUpSave(current, request.control, {
          errorKey: 'admin.prospection.error.failedPrecondition',
          errorKind: kind,
        }),
      );
    }
    return { ok: false, applied: true, error };
  }
}
